// Handles the custom .card-gallery-swatches under each product-card image.
//
// Must live in a real asset file (not a snippet's {% javascript %} block)
// because product recommendations lazy-load card HTML via `innerHTML`, which
// does not execute inline <script> tags. Loading this globally from
// layout/theme.liquid guarantees the click/image-swap logic is always
// available, including for cards injected asynchronously (product
// recommendations, quick-add, infinite scroll, section rendering API, etc.).

(() => {
  if (window.__cardGallerySwatchesBooted) return;
  window.__cardGallerySwatchesBooted = true;

  /** Matches capped widths used on card `product-media` (preview_image_only). */
  const CARD_SRCSET_WIDTHS = [165, 360, 533, 720, 800, 1000, 1200, 1600];

  /** @type {Set<string>} */
  const prefetchedUrls = new Set();

  function buildSized(url, width) {
    if (!url) return url;
    return url.includes('width=')
      ? url.replace(/width=\d+/, `width=${width}`)
      : url + (url.includes('?') ? '&' : '?') + `width=${width}`;
  }

  /**
   * Warm CDN cache before click — pointerenter on swatches.
   * @param {string | undefined} primaryUrl
   * @param {string | undefined} hoverUrl
   */
  function prefetchVariantImages(primaryUrl, hoverUrl) {
    for (const raw of [primaryUrl, hoverUrl]) {
      if (!raw) continue;
      const key = buildSized(raw, 1200);
      if (prefetchedUrls.has(key)) continue;
      prefetchedUrls.add(key);
      const img = new Image();
      img.src = key;
    }
  }

  /**
   * @param {HTMLImageElement | null | undefined} img
   * @param {string} baseUrl
   */
  function applySrcset(img, baseUrl) {
    if (!img || !baseUrl) return;
    img.src = buildSized(baseUrl, 800);
    img.srcset = CARD_SRCSET_WIDTHS.map((w) => `${buildSized(baseUrl, w)} ${w}w`).join(', ');
    img.removeAttribute('loading');
  }

  /**
   * Updates slideshow slide 1 (primary) and slide 2 (hover) when swatches change variant.
   * @param {HTMLElement} gallery .card-gallery
   * @param {string | undefined} primaryUrl data-variant-image
   * @param {string | undefined} hoverUrl data-variant-hover-image
   */
  function swapCardGalleryImages(gallery, primaryUrl, hoverUrl) {
    if (!primaryUrl) return;

    const slides = gallery.querySelectorAll('slideshow-slide');
    const primaryImg = slides[0]?.querySelector('.product-media__image');
    const hoverImg = slides[1]?.querySelector('.product-media__image');

    const secondaryUrl = hoverUrl || primaryUrl;

    requestAnimationFrame(() => {
      applySrcset(primaryImg, primaryUrl);
      if (hoverImg) {
        applySrcset(hoverImg, secondaryUrl);
      }
    });
  }

  function updateCardLinks(gallery, variantUrl) {
    if (!variantUrl) return;
    const galleryLink = gallery.querySelector('[ref="cardGalleryLink"]');
    if (galleryLink) galleryLink.href = variantUrl;

    const cardLink = gallery
      .closest('product-card')
      ?.querySelector('[ref="productCardLink"]');
    if (cardLink) cardLink.href = variantUrl;
  }

  // Pre-order = inventory <= 0 and the variant is still available.
  // Made-to-order badges are tag-driven and must never be overwritten.
  // New In follows variant metafield custom.new_in via data-variant-new-in on each swatch.
  function updateBadge(gallery, isAvailable, inventoryQty, isNewIn) {
    const item = gallery.closest('.product-grid__item') || gallery.closest('li');
    if (!item) return;

    const badge = item.querySelector('.product-card__badge:not(.product-card__badge--new-in)');
    if (!badge) return;

    const newInBadge = item.querySelector('.product-card__badge--new-in');
    const isMadeToOrder = badge.dataset.madeToOrder === 'true';
    const preorderEnabled = badge.dataset.preorderEnabled === 'true';
    const inventoryKnown = typeof inventoryQty === 'number' && !Number.isNaN(inventoryQty);

    function syncNewInPosition() {
      if (!newInBadge) return;
      const primaryHidden = badge.style.display === 'none';
      const newInVisible = newInBadge.style.display !== 'none';
      newInBadge.classList.toggle('product-card__badge--new-in--alone', primaryHidden && newInVisible);
    }

    if (typeof isNewIn === 'boolean' && newInBadge) {
      newInBadge.style.display = isNewIn ? '' : 'none';
    }

    if (isMadeToOrder) {
      syncNewInPosition();
      return;
    }

    if (inventoryKnown && preorderEnabled && isAvailable && inventoryQty <= 0) {
      badge.textContent = badge.dataset.preorderBadgeText || 'Pre-Order';
      badge.style.backgroundColor = badge.dataset.preorderBadgeBg || '#000000';
      badge.style.color = badge.dataset.preorderBadgeColor || '#ffffff';
      badge.style.display = '';
    } else {
      badge.style.display = 'none';
    }

    syncNewInPosition();
  }

  function shouldUseOrderLabel(gallery, isAvailable, inventoryQty) {
    const item = gallery.closest('.product-grid__item') || gallery.closest('li');
    if (!item) return false;

    const badge = item.querySelector('.product-card__badge:not(.product-card__badge--new-in)');
    if (!badge) return false;

    const isMadeToOrder = badge.dataset.madeToOrder === 'true';
    const preorderEnabled = badge.dataset.preorderEnabled === 'true';
    const inventoryKnown = typeof inventoryQty === 'number' && !Number.isNaN(inventoryQty);
    const isPreorder =
      inventoryKnown && preorderEnabled && isAvailable && inventoryQty <= 0;

    return isMadeToOrder || isPreorder;
  }

  function updatePrice(gallery, swatch) {
    const card = gallery.closest('product-card');
    if (!card) return;

    const productPrice = card.querySelector('product-price');
    const priceContainer = productPrice?.querySelector('[ref="priceContainer"]');
    const priceHtml = swatch.dataset.variantPriceHtml;

    if (!priceContainer || !priceHtml) return;

    priceContainer.innerHTML = priceHtml;
    productPrice?.querySelector('[ref="volumePricingNote"]')?.remove();
  }

  function updateAddToCartButton(gallery, isAvailable, inventoryQty) {
    const card = gallery.closest('product-card');
    if (!card) return;

    const btn = card.querySelector('.quick-add__button, [ref="addToCartButton"]');
    const variantInput = card.querySelector('[ref="variantId"]');

    if (btn) {
      btn.disabled = !isAvailable;
      const textEl = btn.querySelector('.add-to-cart-text__content span span');
      if (textEl) {
        const label = shouldUseOrderLabel(gallery, isAvailable, inventoryQty) ? 'Order' : 'Add to Cart';
        textEl.textContent = isAvailable ? label : 'Sold Out';
      }
    }

    if (variantInput) {
      if (isAvailable) {
        variantInput.removeAttribute('disabled');
      } else {
        variantInput.setAttribute('disabled', '');
      }
    }
  }

  // Idempotent — the data-swatch-init gate guarantees we only sync each
  // container's initial state once.
  function syncActiveSwatch(container) {
    if (!container || container.dataset.swatchInit === '1') return;
    container.dataset.swatchInit = '1';

    const activeSwatch = container.querySelector('.card-gallery-swatch--active');
    if (!activeSwatch) return;

    const gallery = container.closest('.card-gallery');
    if (!gallery) return;

    prefetchVariantImages(activeSwatch.dataset.variantImage, activeSwatch.dataset.variantHoverImage);

    const card = gallery.closest('product-card');
    if (card) {
      const variantInput = card.querySelector('[ref="variantId"]');
      if (variantInput) {
        variantInput.value = activeSwatch.dataset.variantId;
        if (activeSwatch.dataset.variantInventory !== undefined) {
          variantInput.dataset.variantInventory = activeSwatch.dataset.variantInventory;
        }
        if (activeSwatch.dataset.variantInventoryPolicy !== undefined) {
          variantInput.dataset.variantInventoryPolicy = activeSwatch.dataset.variantInventoryPolicy;
        }
        if (activeSwatch.dataset.variantPreorderNotice !== undefined) {
          variantInput.dataset.variantPreorderNotice = activeSwatch.dataset.variantPreorderNotice;
        } else {
          delete variantInput.dataset.variantPreorderNotice;
        }
      }
    }
    updateCardLinks(gallery, activeSwatch.dataset.variantUrl);

    const initAvailable = activeSwatch.dataset.variantAvailable === 'true';
    const initInventory = parseInt(activeSwatch.dataset.variantInventory || '0', 10);
    const initNewIn = activeSwatch.dataset.variantNewIn === 'true';
    updateBadge(gallery, initAvailable, initInventory, initNewIn);
    updateAddToCartButton(gallery, initAvailable, initInventory);
    updatePrice(gallery, activeSwatch);
  }

  function initSwatches(root) {
    (root || document)
      .querySelectorAll('.card-gallery-swatches:not([data-swatch-init])')
      .forEach(syncActiveSwatch);
  }

  function readVariantInventoryFromCard(card) {
    const variantInput = card.querySelector('[ref="variantId"]');
    if (!variantInput || variantInput.dataset.variantInventory === undefined) return null;
    const parsed = parseInt(String(variantInput.dataset.variantInventory), 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function syncCardsWithoutSwatches(root) {
    (root || document).querySelectorAll('product-card').forEach((card) => {
      if (!(card instanceof Element)) return;
      const hasSwatches = card.querySelector('.card-gallery-swatches');
      if (hasSwatches) return;

      const gallery = card.querySelector('.card-gallery');
      const btn = card.querySelector('.quick-add__button, [ref="addToCartButton"]');
      if (!gallery || !btn) return;

      const isAvailable = !btn.disabled;
      const inventoryQty = readVariantInventoryFromCard(card);
      updateAddToCartButton(gallery, isAvailable, inventoryQty);
    });
  }

  // Prefetch images when the pointer rests on a swatch (before click).
  document.addEventListener(
    'pointerenter',
    (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const swatch = t.closest('.card-gallery-swatch');
      if (!swatch) return;
      prefetchVariantImages(swatch.dataset.variantImage, swatch.dataset.variantHoverImage);
    },
    true
  );

  // Delegated click handler on the document — catches clicks on any swatch
  // anywhere in the DOM, including cards injected after page load. Capture
  // phase so it runs before product-card.js's navigation click listener.
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const swatch = target.closest('.card-gallery-swatch');
      if (!swatch) return;

      const container = swatch.closest('.card-gallery-swatches');
      const gallery = swatch.closest('.card-gallery');
      if (!container || !gallery) return;

      e.preventDefault();
      e.stopPropagation();

      container
        .querySelectorAll('.card-gallery-swatch')
        .forEach((s) => s.classList.remove('card-gallery-swatch--active'));
      swatch.classList.add('card-gallery-swatch--active');

      prefetchVariantImages(swatch.dataset.variantImage, swatch.dataset.variantHoverImage);
      swapCardGalleryImages(gallery, swatch.dataset.variantImage, swatch.dataset.variantHoverImage);
      updateCardLinks(gallery, swatch.dataset.variantUrl);

      const isAvailable = swatch.dataset.variantAvailable === 'true';
      const inventoryQty = parseInt(swatch.dataset.variantInventory || '0', 10);
      const isNewIn = swatch.dataset.variantNewIn === 'true';
      updateBadge(gallery, isAvailable, inventoryQty, isNewIn);
      updateAddToCartButton(gallery, isAvailable, inventoryQty);
      updatePrice(gallery, swatch);

      const card = gallery.closest('product-card');
      if (card) {
        const variantInput = card.querySelector('[ref="variantId"]');
        if (variantInput) {
          variantInput.value = swatch.dataset.variantId;
          if (swatch.dataset.variantInventory !== undefined) {
            variantInput.dataset.variantInventory = swatch.dataset.variantInventory;
          }
          if (swatch.dataset.variantInventoryPolicy !== undefined) {
            variantInput.dataset.variantInventoryPolicy = swatch.dataset.variantInventoryPolicy;
          }
          if (swatch.dataset.variantPreorderNotice !== undefined) {
            variantInput.dataset.variantPreorderNotice = swatch.dataset.variantPreorderNotice;
          } else {
            delete variantInput.dataset.variantPreorderNotice;
          }
        }
      }
    },
    true
  );

  // Initial boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initSwatches();
      syncCardsWithoutSwatches();
    });
  } else {
    initSwatches();
    syncCardsWithoutSwatches();
  }

  // Theme editor reloads
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target instanceof Element ? e.target : document;
    initSwatches(root);
    syncCardsWithoutSwatches(root);
  });

  // Body-level MutationObserver catches any .card-gallery-swatches added
  // asynchronously — product recommendations, quick-add modal, infinite
  // scroll, ajax filter updates, etc.
  const startObserver = () => {
    if (!document.body) return;
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches('.card-gallery-swatches')) {
            syncActiveSwatch(node);
          }
          if (node.matches('product-card')) {
            syncCardsWithoutSwatches(node);
          }
          node
            .querySelectorAll('.card-gallery-swatches:not([data-swatch-init])')
            .forEach(syncActiveSwatch);
          syncCardsWithoutSwatches(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.body) {
    startObserver();
  } else {
    document.addEventListener('DOMContentLoaded', startObserver);
  }
})();
